import {
  Box, Button, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import PaymentBadge from '../../common/PaymentBadge';
import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useGetChannelOrderQuery } from "../../redux/commonApiCalls";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatCurrency, formatDate, formatNumber } from "../../common/util";
import { convertDate } from "../../helpers/UtilityHelper";
import Loader from "../../common/Loader";
import { apiConstants, postGeneric } from "../../common/constants";
import { ChannelIcon, PushFulfilledToShopify } from "../../helpers/UtilityHelper";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MUIModal from "../../components/MuiModal";
import { LoadingButton } from "@mui/lab";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSnackbar } from "notistack";
import StatusChip from "../../common/StatusChip";
import OrderRowActions from "../../common/OrderRowActions";
import NetraScoreBadge from '../orders/NetraScoreBadge';
import RtoRiskBadge from '../orders/RtoRiskBadge';
import { netraStageNote } from '../orders/netraStageNote';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
  <Box sx={{
    fontSize: 12.5, lineHeight: 1.4,
    color: muted ? "text.secondary" : "text.primary",
    fontWeight: bold ? 600 : 400,
    ...(ellipsis ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {wordBreak: "break-all"}),
  }}>
    {children}
  </Box>
);

const CellStack = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, width: "100%", py: 0.5 }}>
    {children}
  </Box>
);


const BoolBadge = ({ value }) => (
  <Box component="span" sx={{
    display: "inline-block", px: "6px", py: "2px", borderRadius: "3px",
    fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
    bgcolor: value ? "#fef2f2" : "#f0fdf4",
    color: value ? "#dc2626" : "#16a34a",
    border: `1px solid ${value ? "#fecaca" : "#bbf7d0"}`,
  }}>
    {value ? "Yes" : "No"}
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

export default function FulfilledChannelOrders(props) {
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [orderItems, setOrderItems] = useState(null);
  const [skuDetails, setSkuDetails] = useState([]);
  const [finalData, setFinalData] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openR, setOpenR] = useState(false);
  const [res, setRes] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pushFulfilledLoading, setPushFulfilledLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const payload = {
    andfilter: [{ field: "channelOrderStatus", operator: "eq", value: "FULFILLED" }],
    "includes": "orderName,isCodOnHold,paymentMode,createdDate,order_number,total_price,total_weight,total_tax,total_discounts,cod,channelReference,total_discount,total_items_price,fullfilledItems,cancel_reason,merchantName,id,merchantId,orderId,orderItems,channelOrderId,channel,channelOrderStatus,netraAddress,netraRto",
    offset: page * sizePerPage,
    limit: sizePerPage,
    orfilter: [],
  };
  if (props.filters.merchant) {
    payload.andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + props.filters.merchant + '\')' });
  }
  if (props.filters.order_number) {
    payload.andfilter.push({ "field": "order_number", "operator": "eq", "value": props.filters.order_number });
  }
  if (props.filters.order_name) {
    payload.andfilter.push({ "field": "orderName", "operator": "eq", "value": props.filters.order_name });
  }
  if (props.filters.awb_number) {
    payload.andfilter.push({ "field": "proshipOrder.awbNumber", "operator": "eq", "value": props.filters.awb_number });
  }
  if (props.filters.channel) {
    payload.andfilter.push({ "field": "channel", "operator": "eq", "value": props.filters.channel });
  }
  if (props.filters.fromDate) {
    payload.andfilter.push({ field: "createdDate", operator: "gte", value: `Date('${convertDate(props.filters.fromDate)} 00:00:00')` });
  }
  if (props.filters.toDate) {
    payload.andfilter.push({ field: "createdDate", operator: "lte", value: `Date('${convertDate(props.filters.toDate)} 23:59:59')` });
  }

  const { data, isLoading, isFetching, refetch } = useGetChannelOrderQuery(payload);

  useEffect(() => {
    if (data?.data?.length > 0) {
      let skus = data?.data?.map((row) => row.orderItems);
      skus = skus.flat().map((row) => row.sku_id);
      skus = [...new Set(skus)];
      postGeneric(apiConstants.GET_SKU_DIM, { skuList: skus })
        .then((_response) => { setSkuDetails(_response.data); })
        .catch((_err) => { console.log(_err); });
    }
  }, [data]);

  useEffect(() => {
    if (data?.data?.length > 0) {
      const fData = data?.data?.map((row) => {
        const rowItems = row?.orderItems?.map((oi) => {
          const skuDetail = skuDetails?.find((sku) => sku.skuCode === oi.sku_id);
          return { ...oi, ...skuDetail };
        });
        return { ...row, orderItems: rowItems };
      });
      setFinalData(fData);
      props.onPageData?.(fData);
    } else if (data?.data?.length === 0) {
      setFinalData([]);
      props.onPageData?.([]);
    }
  }, [skuDetails, data]);

  const setCODOnHoldFunc = () => {
    const selectedRows = finalData.filter(row => rowSelectionModel.includes(row.id)).map(row => ({
      channelOrderId: row.channelOrderId,
      codOnHold: false,
    }));
    postGeneric(`${apiConstants.UPDATE_CHANNEL_ORDER_STATUS}/${props.filters.merchant}/codOnHold`, selectedRows)
      .then((res) => {
        if (res.status === 200) {
          setOpenR(true);
          setConfirmDialogOpen(false);
          setRowSelectionModel([]);
          setRes(res.data);
          refetch();
        }
      })
      .catch((err) => { console.log("err", err); });
  };


  const columns = [
    {
      field: "channel",
      headerName: "Channel", headerAlign: "left", align: "left",
      sortable: true, width: 120,
      valueGetter: (params) => params.row?.channel || '',
      renderCell: ({ row }) => <ChannelIcon name={row.channel} />,
    },
    {
      field: "merchantName",
      headerName: "Merchant", headerAlign: "left", align: "left",
      sortable: true, minWidth: 200, flex: 1,
      renderCell: ({ row }) => <CellLine bold>{row.merchantName || "—"}</CellLine>,
    },
    {
      field: "order_number",
      headerName: "Order No", headerAlign: "left", align: "left",
      sortable: true, width: 120,
      renderCell: ({ row }) => <CellLine bold>{row.order_number || "—"}</CellLine>,
    },
    {
      field: "orderName",
      headerName: "Order Name", headerAlign: "left", align: "left",
      sortable: false, width: 130,
      renderCell: ({ row }) => <CellLine muted>{row.orderName || "—"}</CellLine>,
    },
    {
      field: "channelOrderId",
      headerName: "Channel Order ID", headerAlign: "left", align: "left",
      sortable: false, minWidth: 180, flex: 1.2,
      renderCell: ({ row }) => <CellLine muted>{row.channelOrderId || "—"}</CellLine>,
    },
    {
      field: "items",
      headerName: "Items", headerAlign: "left", align: "left",
      sortable: false, width: 100,
      renderCell: ({ row }) => (
        <Box
          component="button"
          onClick={() => setOrderItems(row.orderItems)}
          sx={{
            display: "inline-flex", alignItems: "center", px: "8px", py: "3px",
            borderRadius: "4px", fontSize: "11px", fontWeight: 600, cursor: "pointer",
            bgcolor: "transparent", color: "#2563eb", border: "1px solid #bfdbfe",
            transition: "all 0.15s", "&:hover": { bgcolor: "#eff6ff", borderColor: "#93c5fd" },
          }}
        >
          View
        </Box>
      ),
    },
    {
      field: "paymentDetails",
      headerName: "Payment", headerAlign: "left", align: "left",
      sortable: true, minWidth: 130, flex: 1,
      valueGetter: (params) => params.row?.total_price || 0,
      renderCell: ({ row }) => (
        <CellStack>
          {row.total_price && (
            <Box sx={{ fontSize: "13px", fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em" }}>
              {formatCurrency(row.total_price)}
            </Box>
          )}
          <PaymentBadge mode={row.payment_mode} />
        </CellStack>
      ),
    },
    {
      field: "netraScores",
      headerName: "NETRA Score",
      sortable: false,
      hideSortIcons: true,
      headerAlign: "left",
      flex: 1,
      minWidth: 150,
      // Frozen at channel-order ingest, NOT re-derived from the fulfilled
      // shipment — so this can differ from the same shipment's score on the
      // Orders page. The card states the stage explicitly.
      renderCell: ({ row }) => {
        if (!row?.netra_address && !row?.netra_rto) return null;
        const path = `channel-order/${row.id}/netra-score`;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, py: 0.5 }}>
            {row.netra_address && (
              <NetraScoreBadge netra={row.netra_address} orderId={row.id} scorePath={path}
                scoredStageNote={netraStageNote(row.netra_address)} />
            )}
            {row.netra_rto && (
              <RtoRiskBadge netra={row.netra_rto} orderId={row.id} scorePath={path}
                scoredStageNote={netraStageNote(row.netra_rto)} />
            )}
          </Box>
        );
      },
    },
    {
      field: "channelOrderStatus",
      headerName: "Status", headerAlign: "left", align: "left",
      sortable: false, minWidth: 160, flex: 1.1,
      renderCell: ({ row }) => (
        
          
          <StatusChip status={row.channelOrderStatus} />
        
      ),
    },
    {
      field: "createdDate",
      headerName: "Created Date", headerAlign: "left", align: "left",
      sortable: true, minWidth: 170, flex: 1.1,
      valueGetter: (params) => params.row?.createdDate ? new Date(params.row.createdDate).getTime() : 0,
      renderCell: ({ row }) => (
        <CellStack>
          <Box sx={{ fontSize: "11px", color: "#9ca3af" }}>
            {row.createdDate ? formatDate(row.createdDate, "dd MMM, yyyy") : "—"}
          </Box>
        </CellStack>
      ),
    },
    {
      field: "mesurements",
      headerName: "Weight", headerAlign: "left", align: "left",
      sortable: false, width: 120,
      renderCell: ({ row }) => (
        <CellLine muted>{row.total_weight ? `${formatNumber(row.total_weight / 1000)} kg` : "—"}</CellLine>
      ),
    },
    {
      field: "isCodOnHold",
      headerName: "COD On Hold", headerAlign: "left", align: "left",
      sortable: false, width: 110,
      renderCell: ({ row }) => <BoolBadge value={row?.isCodOnHold} />,
    },
    {
      field: "action",
      headerName: "", headerAlign: "center", align: "center",
      sortable: false, width: 60,
      renderCell: ({ row }) => (
        <OrderRowActions
          actions={[
            {
              key: "view",
              label: "View details",
              icon: <VisibilityIcon fontSize="small" />,
              href: `/channel-order-detail/${row.channelOrderId}`,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{""}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to disable COD On Hold for {rowSelectionModel?.length} selected orders?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>No</Button>
          <Button onClick={setCODOnHoldFunc}>Yes</Button>
        </DialogActions>
      </Dialog>

      {(isLoading || isFetching) && <Loader />}

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <LoadingButton
          loading={pushFulfilledLoading}
          disabled={rowSelectionModel?.length === 0}
          onClick={() => PushFulfilledToShopify(rowSelectionModel, finalData, props.filters.merchant, setPushFulfilledLoading, enqueueSnackbar)}
          variant="contained"
          size="small"
          startIcon={<ArrowForwardIcon />}
          sx={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          Push Fulfilled
        </LoadingButton>
        {!rowSelectionModel?.length && (
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            Select a merchant and orders to enable actions
          </Typography>
        )}
      </Box>

      {/* ── Data table ────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <ProzoDataGrid
          columns={columns}
          autoHeight={true}
          rows={finalData}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          checkboxSelection={true}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={(newSelectionModel) => setRowSelectionModel(newSelectionModel)}
          isRowSelectable={(params) => props.filters.merchant}
          filterMode={"server"}
          getRowHeight={() => "auto"}
          rowCount={data?.count}
          getRowId={(row) => row.id}
        />
      </Box>

      {/* ── Order Items modal ─────────────────────────────────────────── */}
      <MUIModal open={orderItems ? true : false} title="Order Items" handleClose={() => setOrderItems(null)}>
        <ProzoDataGrid
          columns={[
            { field: "sku_id", headerName: "SKU", sortable: false, flex: 1 },
            { field: "title", headerName: "Name", sortable: false, flex: 1 },
            { field: "selling_price", headerName: "Price", sortable: false, flex: 1 },
            { field: "units", headerName: "Quantity", sortable: false, flex: 1 },
            {
              field: "tax", headerName: "Tax(%)", sortable: false, flex: 1,
              renderCell: ({ row }) => _.sumBy(row.taxlines, (tax) => tax.rate * 100),
            },
            {
              field: "dimensions", headerName: "Dimensions / Weight", sortable: false, width: 160,
              renderCell: ({ row }) => (
                <CellStack>
                  <CellLine>{[row.length, row.breath, row.height].join(" × ")}</CellLine>
                  <CellLine muted>{formatNumber(row.weight / 1000)} kg</CellLine>
                </CellStack>
              ),
            },
          ]}
          autoHeight={true}
          rows={orderItems || []}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          filterMode={"server"}
          getRowHeight={() => "auto"}
          rowCount={orderItems?.length || 0}
          getRowId={(row) => row.id}
        />
      </MUIModal>

      {/* ── COD On Hold result modal ──────────────────────────────────── */}
      <MUIModal open={openR} title="COD On Hold action details" handleClose={() => setOpenR(false)} maxWidth="md">
        <ProzoDataGrid
          columns={[
            { field: "channelOrderId", headerName: "Channel Order ID", sortable: false, flex: 1 },
            { field: "codOnHold", headerName: "COD On Hold", sortable: false, flex: 1 },
            { field: "message", headerName: "Message", sortable: false, flex: 1 },
          ]}
          autoHeight={true}
          rows={res?.length > 0 ? res.map(row => ({ ...row, id: row.channelOrderId })) : []}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          filterMode={"client"}
          getRowHeight={() => 50}
          rowCount={res?.length || 0}
          getRowId={(row) => row.id}
        />
      </MUIModal>

    </>
  );
}
